import React from 'react';
import { Navigate } from 'react-router-dom';
import { useModulePermissions } from '@/hooks/useModulePermissions';
import { Card, CardContent } from '@/components/ui/card';
import { Shield, AlertCircle } from 'lucide-react';

interface ProtectedModuleProps {
  moduleCode: string;
  children: React.ReactNode;
  fallbackPath?: string;
}

export const ProtectedModule: React.FC<ProtectedModuleProps> = ({
  moduleCode,
  children,
  fallbackPath = '/dashboard'
}) => {
  const { canViewModule, loading } = useModulePermissions();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-2">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-muted-foreground">Verificando permisos...</p>
        </div>
      </div>
    );
  }

  // Si el usuario no puede ver este módulo, redirigir o mostrar mensaje
  if (!canViewModule(moduleCode)) {
    // Si hay una ruta de fallback, redirigir
    if (fallbackPath) {
      return <Navigate to={fallbackPath} replace />;
    }

    // Si no hay ruta de fallback, mostrar mensaje de acceso denegado
    return (
      <div className="flex items-center justify-center min-h-[400px] p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center space-y-4">
            <div className="w-16 h-16 mx-auto bg-destructive/10 rounded-full flex items-center justify-center">
              <Shield className="h-8 w-8 text-destructive" />
            </div>
            <div className="space-y-2">
              <h3 className="text-lg font-semibold">Acceso Restringido</h3>
              <p className="text-muted-foreground text-sm">
                No tienes permisos para acceder a este módulo. Contacta a tu administrador para obtener acceso.
              </p>
            </div>
            <div className="flex items-center justify-center space-x-2 text-xs text-muted-foreground bg-muted/50 rounded-lg p-2">
              <AlertCircle className="h-3 w-3" />
              <span>Módulo: {moduleCode}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Si tiene permisos, renderizar el contenido
  return <>{children}</>;
};